import { useEffect } from "react";

/**
 * Per-route SEO head management for an SPA.
 * Sets <title>, meta description, canonical, OG, Twitter tags, and injects
 * route-scoped JSON-LD. Cleans up its own JSON-LD on unmount so each route
 * only ships its own schema (the global Organization / WebSite / SoftwareApplication
 * JSON-LD in index.html is never touched).
 */
export function useSEO({ title, description, canonical, og, jsonLd, alternates }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description, "name");
    setMeta("og:title", og?.title || title, "property");
    setMeta("og:description", og?.description || description, "property");
    setMeta("og:type", og?.type || "website", "property");
    setMeta("og:url", canonical, "property");
    const ogImage = og?.image || "https://stoicos.ai/og-image.jpg";
    const isCustomImage = !!og?.image;
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:secure_url", ogImage, "property");
    // Declare width/height/type ONLY when they are known to be accurate.
    // A custom image with unknown dimensions must REMOVE the static
    // 1200x630 tags baked into index.html — LinkedIn rejects images whose
    // declared dimensions contradict the actual file.
    const w = isCustomImage ? og?.imageWidth : 1200;
    const h = isCustomImage ? og?.imageHeight : 630;
    const imgType = isCustomImage ? og?.imageType : "image/jpeg";
    setOrRemoveMeta("og:image:width", w ? String(w) : null, "property");
    setOrRemoveMeta("og:image:height", h ? String(h) : null, "property");
    setOrRemoveMeta("og:image:type", imgType || null, "property");
    setMeta("og:site_name", "StoicOs", "property");
    setMeta("twitter:card", "summary_large_image", "name");
    setMeta("twitter:title", og?.title || title, "name");
    setMeta("twitter:description", og?.description || description, "name");
    setMeta("twitter:image", og?.image || "https://stoicos.ai/og-image.jpg", "name");
    setCanonical(canonical);
    setAlternates(alternates);
    setJsonLd(jsonLd);

    return () => {
      // Remove only this route's JSON-LD + hreflang so the next route can install its own.
      document.querySelectorAll('script[data-seo-route="1"]').forEach((el) => el.remove());
      document.querySelectorAll('link[data-seo-route="1"]').forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    canonical,
    JSON.stringify(og || {}),
    JSON.stringify(jsonLd || []),
    JSON.stringify(alternates || []),
  ]);
}

function setOrRemoveMeta(key, content, attr) {
  const el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  setMeta(key, content, attr);
}

function setMeta(key, content, attr) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  if (!url) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

/**
 * Inject `<link rel="alternate" hreflang="…" href="…">` tags for every entry
 * in the alternates array. Route-scoped: any pre-existing hreflang links
 * (baked into index.html for the home route, or previously set by another
 * route) are removed first, so the current route is the sole source of
 * truth for alternate URLs. When `list` is falsy/empty, no changes are
 * made — the previous route's alternates (or the baked-in defaults) are
 * left intact.
 *
 * Pass `[{ lang: "en", href: "…" }, { lang: "nl", href: "…" },
 * { lang: "x-default", href: "…" }]`.
 */
function setAlternates(list) {
  if (!Array.isArray(list) || list.length === 0) return;
  // Wipe every existing hreflang alternate first so we don't accumulate
  // duplicates across routes / with the baked-in defaults in index.html.
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  for (const alt of list) {
    if (!alt || !alt.lang || !alt.href) continue;
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", alt.lang);
    link.setAttribute("href", alt.href);
    link.dataset.seoRoute = "1";
    document.head.appendChild(link);
  }
}

function setJsonLd(data) {
  document.querySelectorAll('script[data-seo-route="1"]').forEach((el) => el.remove());
  if (!data) return;
  const arr = Array.isArray(data) ? data : [data];
  for (const item of arr) {
    if (!item) continue;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seoRoute = "1";
    script.text = JSON.stringify(item);
    document.head.appendChild(script);
  }
}
